import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Swal from "sweetalert2";

const API =
  "https://wwwa.isepsantafe.net.ar/expedientes/docentes";

const DIAS = [
  { value: "LUNES", label: "Lun", completo: "Lunes" },
  { value: "MARTES", label: "Mar", completo: "Martes" },
  { value: "MIERCOLES", label: "Mié", completo: "Miércoles" },
  { value: "JUEVES", label: "Jue", completo: "Jueves" },
  { value: "VIERNES", label: "Vie", completo: "Viernes" },
  { value: "SABADO", label: "Sáb", completo: "Sábado" },
  { value: "DOMINGO", label: "Dom", completo: "Domingo" },
];

const OPCIONES_INTERVALO = [
  {
    value: 1,
    titulo: "Todas las semanas",
    descripcion: "Se repite semanalmente",
  },
  {
    value: 2,
    titulo: "Semana por medio",
    descripcion: "Se repite cada 2 semanas",
  },
  {
    value: 3,
    titulo: "Cada 3 semanas",
    descripcion: "Se repite cada tres semanas",
  },
  {
    value: 4,
    titulo: "Cada 4 semanas",
    descripcion: "Se repite cada cuatro semanas",
  },
];

const obtenerFechaActual = () => {
  const fecha = new Date();

  const anio = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");

  return `${anio}-${mes}-${dia}`;
};

const formatearFecha = (fecha) => {
  if (!fecha) return "";

  const [anio, mes, dia] = fecha.split("-");

  if (!anio || !mes || !dia) {
    return fecha;
  }

  return `${dia}/${mes}/${anio}`;
};

const HorariosModal = ({
  asignacionId,
  onClose,
  onGuardado,
}) => {
  const [diasSeleccionados, setDiasSeleccionados] =
    useState([]);

  const [horarios, setHorarios] = useState({});
  const [intervalo, setIntervalo] = useState(1);

  const [
    fechaInicioRepeticion,
    setFechaInicioRepeticion,
  ] = useState(obtenerFechaActual());

  const [loading, setLoading] = useState(false);
  const [loadingInicial, setLoadingInicial] =
    useState(true);

  const [errorInicial, setErrorInicial] =
    useState("");

  /* =========================================================
     BLOQUEAR SCROLL DEL BODY
     ========================================================= */

  useEffect(() => {
    const overflowAnterior =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        overflowAnterior;
    };
  }, []);

  /* =========================================================
     CERRAR CON ESCAPE
     ========================================================= */

  useEffect(() => {
    const cerrarConEscape = (event) => {
      if (event.key === "Escape" && !loading) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      cerrarConEscape
    );

    return () => {
      window.removeEventListener(
        "keydown",
        cerrarConEscape
      );
    };
  }, [loading, onClose]);

  /* =========================================================
     CARGAR HORARIOS
     ========================================================= */

  useEffect(() => {
    if (!asignacionId) {
      setErrorInicial(
        "No se recibió una asignación válida."
      );

      setLoadingInicial(false);
      return;
    }

    const fetchHorarios = async () => {
      try {
        setLoadingInicial(true);
        setErrorInicial("");

        const respuesta = await axios.post(
          `${API}/obtenerHorariosPorAsignacion/${asignacionId}`
        );

        const data = Array.isArray(
          respuesta.data?.data
        )
          ? respuesta.data.data
          : [];

        if (data.length === 0) {
          return;
        }

        const dias = [];
        const horariosObj = {};

        data.forEach((horario) => {
          if (!horario?.diaSemana) return;

          if (!dias.includes(horario.diaSemana)) {
            dias.push(horario.diaSemana);
          }

          horariosObj[horario.diaSemana] = {
            horaInicio:
              horario.horaInicio?.slice(0, 5) || "",
            horaFin:
              horario.horaFin?.slice(0, 5) || "",
          };
        });

        setDiasSeleccionados(dias);
        setHorarios(horariosObj);

        if (data[0]?.intervalo) {
          setIntervalo(
            Number(data[0].intervalo)
          );
        }

        const fechaGuardada =
          data[0]?.fechaInicioRepeticion ||
          data[0]?.fechaInicio ||
          data[0]?.fechaReferencia ||
          null;

        if (fechaGuardada) {
          setFechaInicioRepeticion(
            String(fechaGuardada).slice(0, 10)
          );
        }
      } catch (error) {
        console.error(
          "Error cargando horarios:",
          error
        );

        setErrorInicial(
          error.response?.data?.message ||
            error.response?.data?.msg ||
            "No se pudieron cargar los horarios existentes."
        );
      } finally {
        setLoadingInicial(false);
      }
    };

    fetchHorarios();
  }, [asignacionId]);

  /* =========================================================
     SELECCIONAR DÍA
     ========================================================= */

  const toggleDia = (dia) => {
    setDiasSeleccionados((prev) => {
      if (prev.includes(dia)) {
        setHorarios((horariosPrevios) => {
          const copia = {
            ...horariosPrevios,
          };

          delete copia[dia];

          return copia;
        });

        return prev.filter(
          (item) => item !== dia
        );
      }

      setHorarios((horariosPrevios) => ({
        ...horariosPrevios,

        [dia]: {
          horaInicio:
            horariosPrevios[dia]?.horaInicio ||
            "",

          horaFin:
            horariosPrevios[dia]?.horaFin || "",
        },
      }));

      return [...prev, dia];
    });
  };

  /* =========================================================
     MODIFICAR HORARIO
     ========================================================= */

  const setHorario = (
    dia,
    campo,
    valor
  ) => {
    setHorarios((prev) => ({
      ...prev,

      [dia]: {
        horaInicio:
          prev[dia]?.horaInicio || "",

        horaFin:
          prev[dia]?.horaFin || "",

        [campo]: valor,
      },
    }));
  };

  /* =========================================================
     COPIAR PRIMER HORARIO
     ========================================================= */

  const copiarPrimerHorario = () => {
    if (diasSeleccionados.length < 2) {
      return;
    }

    const primerDia =
      diasSeleccionados[0];

    const primerHorario =
      horarios[primerDia];

    if (
      !primerHorario?.horaInicio ||
      !primerHorario?.horaFin
    ) {
      Swal.fire({
        icon: "info",
        title: "Complete el primer horario",
        text: "Ingrese primero la hora de inicio y finalización.",
        confirmButtonColor: "#3659a1",
      });

      return;
    }

    setHorarios((prev) => {
      const nuevosHorarios = {
        ...prev,
      };

      diasSeleccionados.forEach((dia) => {
        nuevosHorarios[dia] = {
          horaInicio:
            primerHorario.horaInicio,

          horaFin:
            primerHorario.horaFin,
        };
      });

      return nuevosHorarios;
    });
  };

  /* =========================================================
     VALIDACIONES
     ========================================================= */

  const erroresValidacion = useMemo(() => {
    const errores = [];

    if (diasSeleccionados.length === 0) {
      errores.push(
        "Seleccione al menos un día."
      );
    }

    diasSeleccionados.forEach((dia) => {
      const horaInicio =
        horarios[dia]?.horaInicio;

      const horaFin =
        horarios[dia]?.horaFin;

      const infoDia = DIAS.find(
        (item) => item.value === dia
      );

      const nombreDia =
        infoDia?.completo ||
        dia.toLowerCase();

      if (!horaInicio || !horaFin) {
        errores.push(
          `Complete el horario correspondiente a ${nombreDia}.`
        );

        return;
      }

      if (horaInicio >= horaFin) {
        errores.push(
          `La hora de finalización de ${nombreDia} debe ser posterior a la hora de inicio.`
        );
      }
    });

    if (!fechaInicioRepeticion) {
      errores.push(
        "Seleccione desde qué fecha comienza la repetición."
      );
    }

    return errores;
  }, [
    diasSeleccionados,
    horarios,
    fechaInicioRepeticion,
  ]);

  /* =========================================================
     GUARDAR
     ========================================================= */

  const guardar = async () => {
    if (erroresValidacion.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Revise los horarios",

        html: erroresValidacion
          .map(
            (error) =>
              `<div style="
                margin-bottom:7px;
                text-align:left;
              ">
                • ${error}
              </div>`
          )
          .join(""),

        confirmButtonColor: "#3659a1",
      });

      return;
    }

    try {
      setLoading(true);

      const data = diasSeleccionados.map(
        (dia) => ({
          diaSemana: dia,

          horaInicio:
            horarios[dia].horaInicio,

          horaFin:
            horarios[dia].horaFin,

          intervalo,

          fechaInicioRepeticion,
        })
      );

      await axios.post(
        `${API}/cargarHorariosAsignacion`,
        {
          asignacionId,
          intervalo,
          fechaInicioRepeticion,
          horarios: data,
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Horarios guardados",
        text: "La configuración se guardó correctamente.",
        timer: 1700,
        showConfirmButton: false,
      });

      if (
        typeof onGuardado === "function"
      ) {
        await onGuardado();
      }

      onClose?.();
    } catch (error) {
      console.error(
        "Error guardando horarios:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "No se pudo guardar",

        text:
          error.response?.data?.message ||
          error.response?.data?.msg ||
          "Ocurrió un error al guardar los horarios.",

        confirmButtonColor: "#3659a1",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     CERRAR AL TOCAR EL OVERLAY
     ========================================================= */

  const cerrarConOverlay = (event) => {
    if (
      event.target === event.currentTarget &&
      !loading
    ) {
      onClose?.();
    }
  };

  const repeticionSeleccionada =
    OPCIONES_INTERVALO.find(
      (opcion) =>
        opcion.value === intervalo
    );

  /* =========================================================
     LOADING
     ========================================================= */

  if (loadingInicial) {
    return createPortal(
      <div style={styles.overlay}>
        <div style={styles.loadingCard}>
          <div style={styles.spinner} />

          <strong style={styles.loadingTitle}>
            Cargando horarios
          </strong>

          <span style={styles.loadingText}>
            Recuperando la configuración
            existente...
          </span>
        </div>

        <style>
          {`
            @keyframes horarioModalSpin {
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </div>,
      document.body
    );
  }

  /* =========================================================
     MODAL
     ========================================================= */

  const contenidoModal = (
    <div
      style={styles.overlay}
      onMouseDown={cerrarConOverlay}
      role="presentation"
    >
      <div
        style={styles.modal}
        onMouseDown={(event) =>
          event.stopPropagation()
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="horarios-modal-title"
      >
        {/* HEADER */}

        <div style={styles.header}>
          <div style={styles.headerContent}>
            <span style={styles.eyebrow}>
              ASIGNACIÓN DOCENTE
            </span>

            <h2
              id="horarios-modal-title"
              style={styles.title}
            >
              Configurar horarios
            </h2>

            <p style={styles.subtitle}>
              Seleccione los días, horarios y
              frecuencia de cursado.
            </p>
          </div>

          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={loading}
            style={styles.closeButton}
            aria-label="Cerrar"
            title="Cerrar"
          >
            ×
          </button>
        </div>

        {/* BODY */}

        <div style={styles.body}>
          {errorInicial && (
            <div style={styles.errorBox}>
              <strong>
                No se pudieron cargar los datos.
              </strong>

              <span>{errorInicial}</span>
            </div>
          )}

          {/* PASO 1 */}

          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <div>
                <span style={styles.step}>
                  01
                </span>

                <h3
                  style={styles.sectionTitle}
                >
                  Días de cursado
                </h3>
              </div>

              <span style={styles.counter}>
                {diasSeleccionados.length}{" "}
                seleccionados
              </span>
            </div>

            <div style={styles.diasContainer}>
              {DIAS.map((dia) => {
                const seleccionado =
                  diasSeleccionados.includes(
                    dia.value
                  );

                return (
                  <button
                    key={dia.value}
                    type="button"
                    onClick={() =>
                      toggleDia(dia.value)
                    }
                    style={{
                      ...styles.diaBtn,

                      ...(seleccionado
                        ? styles.diaBtnActive
                        : styles.diaBtnInactive),
                    }}
                  >
                    <span
                      style={
                        styles.diaAbreviado
                      }
                    >
                      {dia.label}
                    </span>

                    <span
                      style={
                        styles.diaCompleto
                      }
                    >
                      {dia.completo}
                    </span>

                    {seleccionado && (
                      <span
                        style={styles.checkDia}
                      >
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* PASO 2 */}

          {diasSeleccionados.length > 0 && (
            <section style={styles.section}>
              <div
                style={styles.sectionHeader}
              >
                <div>
                  <span style={styles.step}>
                    02
                  </span>

                  <h3
                    style={
                      styles.sectionTitle
                    }
                  >
                    Carga horaria
                  </h3>
                </div>

                {diasSeleccionados.length >
                  1 && (
                  <button
                    type="button"
                    onClick={
                      copiarPrimerHorario
                    }
                    style={
                      styles.copyButton
                    }
                  >
                    Copiar primer horario
                  </button>
                )}
              </div>

              <div
                style={styles.horariosList}
              >
                {diasSeleccionados.map(
                  (dia) => {
                    const infoDia = DIAS.find(
                      (item) =>
                        item.value === dia
                    );

                    return (
                      <div
                        key={dia}
                        style={
                          styles.horarioCard
                        }
                      >
                        <div
                          style={
                            styles.diaHorario
                          }
                        >
                          <span
                            style={
                              styles.diaCircle
                            }
                          >
                            {infoDia?.label?.charAt(
                              0
                            )}
                          </span>

                          <div>
                            <strong
                              style={
                                styles.diaHorarioNombre
                              }
                            >
                              {infoDia?.completo ||
                                dia}
                            </strong>

                            <span
                              style={
                                styles.diaHorarioTexto
                              }
                            >
                              Horario de cursado
                            </span>
                          </div>
                        </div>

                        <div
                          style={
                            styles.timeGroup
                          }
                        >
                          <label
                            style={
                              styles.timeField
                            }
                          >
                            <span
                              style={
                                styles.inputLabel
                              }
                            >
                              Desde
                            </span>

                            <input
                              type="time"
                              value={
                                horarios[dia]
                                  ?.horaInicio ||
                                ""
                              }
                              onChange={(
                                event
                              ) =>
                                setHorario(
                                  dia,
                                  "horaInicio",
                                  event.target
                                    .value
                                )
                              }
                              style={
                                styles.timeInput
                              }
                            />
                          </label>

                          <span
                            style={
                              styles.timeSeparator
                            }
                          >
                            →
                          </span>

                          <label
                            style={
                              styles.timeField
                            }
                          >
                            <span
                              style={
                                styles.inputLabel
                              }
                            >
                              Hasta
                            </span>

                            <input
                              type="time"
                              value={
                                horarios[dia]
                                  ?.horaFin ||
                                ""
                              }
                              onChange={(
                                event
                              ) =>
                                setHorario(
                                  dia,
                                  "horaFin",
                                  event.target
                                    .value
                                )
                              }
                              style={
                                styles.timeInput
                              }
                            />
                          </label>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </section>
          )}

          {/* PASO 3 */}

          {diasSeleccionados.length > 0 && (
            <section style={styles.section}>
              <div
                style={styles.sectionHeader}
              >
                <div>
                  <span style={styles.step}>
                    03
                  </span>

                  <h3
                    style={
                      styles.sectionTitle
                    }
                  >
                    Frecuencia
                  </h3>
                </div>
              </div>

              <div
                style={styles.intervalosGrid}
              >
                {OPCIONES_INTERVALO.map(
                  (opcion) => {
                    const seleccionado =
                      intervalo ===
                      opcion.value;

                    return (
                      <button
                        key={opcion.value}
                        type="button"
                        onClick={() =>
                          setIntervalo(
                            opcion.value
                          )
                        }
                        style={{
                          ...styles.intervaloCard,

                          ...(seleccionado
                            ? styles.intervaloCardActive
                            : {}),
                        }}
                      >
                        <span
                          style={{
                            ...styles.radio,

                            ...(seleccionado
                              ? styles.radioActive
                              : {}),
                          }}
                        >
                          {seleccionado && (
                            <span
                              style={
                                styles.radioPoint
                              }
                            />
                          )}
                        </span>

                        <span
                          style={
                            styles.intervaloContent
                          }
                        >
                          <strong
                            style={
                              styles.intervaloTitle
                            }
                          >
                            {opcion.titulo}
                          </strong>

                          <span
                            style={
                              styles.intervaloDescription
                            }
                          >
                            {
                              opcion.descripcion
                            }
                          </span>
                        </span>
                      </button>
                    );
                  }
                )}
              </div>

              <div style={styles.fechaCard}>
                <div style={styles.fechaIcon}>
                  📅
                </div>

                <div
                  style={styles.fechaContent}
                >
                  <label
                    style={styles.fechaLabel}
                  >
                    Comenzar la repetición
                    desde
                  </label>

                  <p
                    style={styles.fechaHelp}
                  >
                    Esta fecha define cuál será
                    la primera semana del
                    cronograma.
                  </p>
                </div>

                <input
                  type="date"
                  value={
                    fechaInicioRepeticion
                  }
                  onChange={(event) =>
                    setFechaInicioRepeticion(
                      event.target.value
                    )
                  }
                  style={styles.dateInput}
                />
              </div>

              <div style={styles.resumen}>
                <span
                  style={styles.resumenIcon}
                >
                  ↻
                </span>

                <div>
                  <strong
                    style={
                      styles.resumenTitle
                    }
                  >
                    {
                      repeticionSeleccionada?.titulo
                    }
                  </strong>

                  <span
                    style={styles.resumenText}
                  >
                    La repetición comenzará el{" "}
                    <strong>
                      {formatearFecha(
                        fechaInicioRepeticion
                      )}
                    </strong>

                    {intervalo > 1
                      ? ` y continuará cada ${intervalo} semanas.`
                      : " y continuará semanalmente."}
                  </span>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* FOOTER */}

        <div style={styles.footer}>
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={loading}
            style={styles.cancelButton}
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={guardar}
            disabled={
              loading ||
              diasSeleccionados.length === 0
            }
            style={{
              ...styles.saveButton,

              ...(loading ||
              diasSeleccionados.length === 0
                ? styles.saveButtonDisabled
                : {}),
            }}
          >
            {loading ? (
              <>
                <span
                  style={styles.smallSpinner}
                />

                Guardando...
              </>
            ) : (
              <>
                <span>✓</span>

                Guardar horarios
              </>
            )}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes horarioModalSpin {
            to {
              transform: rotate(360deg);
            }
          }

          @media (max-width: 560px) {
            .horarios-modal-input {
              width: 100%;
            }
          }
        `}
      </style>
    </div>
  );

  return createPortal(
    contenidoModal,
    document.body
  );
};

export default HorariosModal;

/* =========================================================
   ESTILOS
   ========================================================= */

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999999,

    width: "100vw",
    height: "100dvh",

    margin: 0,
    padding: "16px",

    boxSizing: "border-box",

    background:
      "linear-gradient(135deg, rgba(18, 33, 63, 0.76), rgba(20, 48, 94, 0.67))",

    backdropFilter: "blur(7px)",
    WebkitBackdropFilter: "blur(7px)",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    overflow: "hidden",
  },

  modal: {
    position: "relative",

    width: "760px",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100dvh - 32px)",

    margin: "0 auto",

    background: "#f7f9fc",

    borderRadius: "22px",
    border:
      "1px solid rgba(255,255,255,0.8)",

    boxShadow:
      "0 28px 80px rgba(12, 28, 57, 0.38)",

    overflow: "hidden",

    display: "flex",
    flexDirection: "column",

    boxSizing: "border-box",
  },

  header: {
    width: "100%",

    padding: "20px 22px 17px",

    background:
      "linear-gradient(135deg, #274d91 0%, #365fa7 55%, #4471bd 100%)",

    color: "#fff",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    gap: "16px",
    flexShrink: 0,

    boxSizing: "border-box",
  },

  headerContent: {
    flex: 1,
    minWidth: 0,
  },

  eyebrow: {
    display: "block",

    marginBottom: "5px",

    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "1.8px",

    opacity: 0.72,
  },

  title: {
    margin: 0,

    fontSize: "clamp(20px, 4vw, 24px)",
    fontWeight: 800,
    letterSpacing: "-0.5px",
  },

  subtitle: {
    margin: "6px 0 0",

    fontSize: "13px",
    lineHeight: 1.5,

    opacity: 0.82,
  },

  closeButton: {
    width: "36px",
    height: "36px",

    flexShrink: 0,

    borderRadius: "11px",
    border:
      "1px solid rgba(255,255,255,0.25)",

    background:
      "rgba(255,255,255,0.12)",

    color: "#fff",

    fontSize: "25px",
    lineHeight: 1,

    cursor: "pointer",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  body: {
    width: "100%",

    flex: "1 1 auto",
    minHeight: 0,

    overflowY: "auto",
    overflowX: "hidden",

    padding: "18px",

    boxSizing: "border-box",

    overscrollBehavior: "contain",
  },

  section: {
    width: "100%",

    marginBottom: "13px",
    padding: "16px",

    background: "#fff",

    borderRadius: "16px",
    border: "1px solid #e8edf5",

    boxShadow:
      "0 5px 18px rgba(34, 59, 101, 0.05)",

    boxSizing: "border-box",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",

    gap: "10px",
    marginBottom: "14px",
  },

  step: {
    display: "block",

    marginBottom: "2px",

    color: "#6f88b2",

    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "1.2px",
  },

  sectionTitle: {
    margin: 0,

    color: "#203557",

    fontSize: "16px",
    fontWeight: 800,
  },

  counter: {
    padding: "6px 10px",

    background: "#edf3ff",
    color: "#365fa7",

    borderRadius: "999px",

    fontSize: "11px",
    fontWeight: 800,

    whiteSpace: "nowrap",
  },

  diasContainer: {
    width: "100%",

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(76px, 1fr))",

    gap: "8px",

    boxSizing: "border-box",
  },

  diaBtn: {
    position: "relative",

    width: "100%",
    minWidth: 0,
    minHeight: "61px",

    padding: "9px 7px",

    borderRadius: "13px",

    cursor: "pointer",

    transition: "all .2s ease",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",

    boxSizing: "border-box",
  },

  diaBtnInactive: {
    border: "1px solid #dce4ef",

    background: "#f8fafc",
    color: "#60708b",
  },

  diaBtnActive: {
    border: "1px solid #365fa7",

    background:
      "linear-gradient(145deg, #edf4ff, #e3edff)",

    color: "#274d91",

    boxShadow:
      "0 5px 13px rgba(54, 95, 167, 0.14)",
  },

  diaAbreviado: {
    fontSize: "15px",
    fontWeight: 900,
  },

  diaCompleto: {
    marginTop: "2px",

    fontSize: "10px",

    opacity: 0.72,
  },

  checkDia: {
    position: "absolute",

    top: "5px",
    right: "7px",

    width: "17px",
    height: "17px",

    borderRadius: "50%",

    background: "#365fa7",
    color: "#fff",

    fontSize: "10px",
    fontWeight: 900,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  copyButton: {
    padding: "7px 10px",

    border: "none",
    borderRadius: "9px",

    background: "#edf3ff",
    color: "#365fa7",

    fontSize: "11px",
    fontWeight: 800,

    cursor: "pointer",
  },

  horariosList: {
    width: "100%",

    display: "flex",
    flexDirection: "column",

    gap: "9px",
  },

  horarioCard: {
    width: "100%",

    padding: "11px 12px",

    background: "#f8fafc",

    border: "1px solid #e4eaf3",
    borderRadius: "13px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",

    gap: "13px",

    boxSizing: "border-box",
  },

  diaHorario: {
    minWidth: "145px",

    display: "flex",
    alignItems: "center",

    gap: "10px",
  },

  diaCircle: {
    width: "34px",
    height: "34px",

    flexShrink: 0,

    borderRadius: "11px",

    background: "#e6efff",
    color: "#365fa7",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    fontSize: "14px",
    fontWeight: 900,
  },

  diaHorarioNombre: {
    display: "block",

    color: "#203557",

    fontSize: "13px",
  },

  diaHorarioTexto: {
    display: "block",

    marginTop: "1px",

    color: "#8491a5",

    fontSize: "10px",
  },

  timeGroup: {
    flex: 1,
    minWidth: "255px",

    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",

    gap: "7px",
  },

  timeField: {
    flex: 1,
    maxWidth: "135px",

    display: "flex",
    flexDirection: "column",

    gap: "4px",
  },

  inputLabel: {
    color: "#7e8ba0",

    fontSize: "9px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".6px",
  },

  timeInput: {
    width: "100%",
    minWidth: 0,
    height: "38px",

    padding: "0 8px",

    border: "1px solid #ccd7e6",
    borderRadius: "9px",

    background: "#fff",
    color: "#203557",

    fontSize: "13px",
    fontWeight: 700,

    outline: "none",

    boxSizing: "border-box",
  },

  timeSeparator: {
    paddingBottom: "9px",

    color: "#91a0b6",

    fontWeight: 900,

    flexShrink: 0,
  },

  intervalosGrid: {
    width: "100%",

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(210px, 1fr))",

    gap: "9px",
  },

  intervaloCard: {
    width: "100%",
    minWidth: 0,
    minHeight: "68px",

    padding: "11px",

    border: "1px solid #dfe6f0",
    borderRadius: "13px",

    background: "#fafbfd",

    textAlign: "left",

    cursor: "pointer",

    display: "flex",
    alignItems: "flex-start",

    gap: "10px",

    boxSizing: "border-box",
  },

  intervaloCardActive: {
    border: "1px solid #365fa7",

    background: "#edf4ff",

    boxShadow:
      "0 5px 14px rgba(54, 95, 167, 0.11)",
  },

  radio: {
    width: "18px",
    height: "18px",

    marginTop: "1px",

    borderRadius: "50%",
    border: "2px solid #b8c4d4",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    flexShrink: 0,

    boxSizing: "border-box",
  },

  radioActive: {
    borderColor: "#365fa7",
  },

  radioPoint: {
    width: "8px",
    height: "8px",

    borderRadius: "50%",

    background: "#365fa7",
  },

  intervaloContent: {
    minWidth: 0,

    display: "flex",
    flexDirection: "column",

    gap: "3px",
  },

  intervaloTitle: {
    color: "#273b5d",

    fontSize: "12px",
  },

  intervaloDescription: {
    color: "#8491a5",

    fontSize: "10px",
    lineHeight: 1.4,
  },

  fechaCard: {
    width: "100%",

    marginTop: "12px",
    padding: "12px",

    borderRadius: "13px",
    border: "1px solid #e0e7f1",

    background: "#f4f7fb",

    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",

    gap: "11px",

    boxSizing: "border-box",
  },

  fechaIcon: {
    width: "38px",
    height: "38px",

    flexShrink: 0,

    borderRadius: "12px",

    background: "#e5edfb",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    fontSize: "17px",
  },

  fechaContent: {
    flex: 1,

    minWidth: "180px",
  },

  fechaLabel: {
    display: "block",

    color: "#273b5d",

    fontSize: "12px",
    fontWeight: 800,
  },

  fechaHelp: {
    margin: "3px 0 0",

    color: "#8491a5",

    fontSize: "10px",
  },

  dateInput: {
    height: "40px",
    maxWidth: "100%",

    padding: "0 11px",

    border: "1px solid #cbd7e6",
    borderRadius: "10px",

    background: "#fff",
    color: "#273b5d",

    fontSize: "12px",
    fontWeight: 700,

    outline: "none",

    boxSizing: "border-box",
  },

  resumen: {
    width: "100%",

    marginTop: "10px",
    padding: "11px 13px",

    borderRadius: "12px",
    border: "1px solid #ccebdd",

    background: "#eaf7f0",
    color: "#286144",

    display: "flex",
    alignItems: "center",

    gap: "10px",

    boxSizing: "border-box",
  },

  resumenIcon: {
    fontSize: "21px",
    fontWeight: 900,

    flexShrink: 0,
  },

  resumenTitle: {
    display: "block",

    fontSize: "11px",
  },

  resumenText: {
    display: "block",

    marginTop: "2px",

    fontSize: "10px",
    lineHeight: 1.4,
  },

  errorBox: {
    width: "100%",

    marginBottom: "13px",
    padding: "11px 13px",

    background: "#fff1f1",
    color: "#9d3030",

    border: "1px solid #f3cccc",
    borderRadius: "12px",

    display: "flex",
    flexDirection: "column",

    gap: "4px",

    fontSize: "11px",

    boxSizing: "border-box",
  },

  footer: {
    width: "100%",

    padding: "14px 18px",

    background: "#fff",

    borderTop: "1px solid #e3e9f1",

    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",

    gap: "9px",

    flexShrink: 0,

    boxSizing: "border-box",
  },

  cancelButton: {
    height: "41px",

    padding: "0 17px",

    borderRadius: "10px",
    border: "1px solid #d5deea",

    background: "#fff",
    color: "#64738a",

    fontSize: "12px",
    fontWeight: 800,

    cursor: "pointer",
  },

  saveButton: {
    minWidth: "155px",
    height: "41px",

    padding: "0 18px",

    borderRadius: "10px",
    border: "none",

    background:
      "linear-gradient(135deg, #315995, #416db2)",

    color: "#fff",

    fontSize: "12px",
    fontWeight: 800,

    cursor: "pointer",

    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",

    gap: "7px",

    boxShadow:
      "0 7px 16px rgba(49, 89, 149, 0.22)",
  },

  saveButtonDisabled: {
    opacity: 0.55,

    cursor: "not-allowed",

    boxShadow: "none",
  },

  loadingCard: {
    width: "min(320px, calc(100vw - 32px))",

    padding: "28px",

    background: "#fff",

    borderRadius: "18px",

    boxShadow:
      "0 25px 70px rgba(10, 25, 50, 0.3)",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    gap: "8px",

    boxSizing: "border-box",
  },

  spinner: {
    width: "33px",
    height: "33px",

    borderRadius: "50%",

    border: "4px solid #dfe7f3",
    borderTopColor: "#365fa7",

    animation:
      "horarioModalSpin 0.8s linear infinite",
  },

  smallSpinner: {
    width: "14px",
    height: "14px",

    borderRadius: "50%",

    border:
      "2px solid rgba(255,255,255,.4)",

    borderTopColor: "#fff",

    animation:
      "horarioModalSpin 0.8s linear infinite",
  },

  loadingTitle: {
    marginTop: "5px",

    color: "#253b5e",

    fontSize: "15px",
  },

  loadingText: {
    color: "#8090a7",

    fontSize: "11px",
    textAlign: "center",
  },
};